class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.addEventListener('click', this.handleUpsellAdd.bind(this));
    this.addEventListener('change', this.handleUpsellVariantChange.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
    this.initializeUpsells();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }



  async initializeUpsells() {
    const upsellSection = this.querySelector('[data-cart-drawer-upsell]');
    if (!upsellSection) return;

    const upsellList = upsellSection.querySelector('[data-upsell-list]');
    const collectionHandle = upsellSection.dataset.upsellCollectionHandle;
    const cartProductIds = (upsellSection.dataset.cartProductIds || '')
      .split(',')
      .map((id) => id.trim())
      .filter(Boolean);

    if (!upsellList || !collectionHandle) return;

    upsellSection.hidden = true;
    upsellList.innerHTML = '';

    try {
      const collectionResponse = await this.requestUpsellJson(
        this.getUpsellStoreUrl('collections/' + collectionHandle + '/products.json?limit=50')
      );
      if (!collectionResponse.ok) throw new Error('Unable to load upsells.');

      let sourceProducts = collectionResponse.data.products || [];

      // Keep the upsell working if the configured collection exists but has not
      // been populated yet. Only published accessory products remain eligible.
      if (!sourceProducts.length) {
        const productsResponse = await this.requestUpsellJson(this.getUpsellStoreUrl('products.json?limit=250'));
        if (!productsResponse.ok) throw new Error('Unable to load fallback upsells.');

        sourceProducts = (productsResponse.data.products || []).filter((product) =>
          String(product.product_type || '').toLowerCase().includes('accessory')
        );
      }

      const upsellProducts = this.getEligibleUpsellProducts(sourceProducts, cartProductIds).slice(0, 3);

      if (!upsellProducts.length) return;

      upsellList.innerHTML = upsellProducts.map((product) => this.getUpsellItemMarkup(product)).join('');
      upsellSection.hidden = false;
    } catch (error) {
      upsellSection.hidden = true;
      upsellList.innerHTML = '';
    }
  }

  getEligibleUpsellProducts(products, cartProductIds) {
    return products
      .filter((product) => !cartProductIds.includes(String(product.id)))
      .map((product) => {
        const eligibleVariants = (product.variants || [])
          .filter((variant) => variant.available && Number(variant.price) <= 10)
          .map((variant) => ({
            id: variant.id,
            title: variant.title,
            price: Number(variant.price),
            priceText: this.formatUpsellMoney(Number(variant.price)),
          }));

        if (!eligibleVariants.length) return null;

        const imageUrl =
          product.featured_image?.src ||
          product.image?.src ||
          product.image ||
          product.images?.[0]?.src ||
          product.images?.[0] ||
          '';

        return {
          id: product.id,
          title: product.title,
          url: this.getUpsellStoreUrl('products/' + product.handle),
          featuredImage: imageUrl,
          defaultPriceText:
            eligibleVariants.length > 1
              ? 'From ' + this.formatUpsellMoney(Math.min(...eligibleVariants.map((variant) => variant.price)))
              : eligibleVariants[0].priceText,
          variants: eligibleVariants,
        };
      })
      .filter(Boolean);
  }

  getUpsellItemMarkup(product) {
    const hasMultipleVariants = product.variants.length > 1;
    const imageMarkup = product.featuredImage
      ? '<img src="' + this.escapeUpsellHtml(product.featuredImage) + '" alt="' + this.escapeUpsellHtml(product.title) + '" loading="lazy" width="96">'
      : '';

    const variantSelectMarkup = hasMultipleVariants
      ? '<label class="visually-hidden" for="CartDrawer-Upsell-Variant-' + product.id + '">Choose an option for ' + this.escapeUpsellHtml(product.title) + '</label>'
        + '<select id="CartDrawer-Upsell-Variant-' + product.id + '" class="cart-drawer-upsell__variant-select" data-upsell-variant-select>'
        + '<option value="">Choose an option</option>'
        + product.variants.map((variant) => '<option value="' + variant.id + '" data-price="' + this.escapeUpsellHtml(variant.priceText) + '">' + this.escapeUpsellHtml(variant.title) + ' - ' + this.escapeUpsellHtml(variant.priceText) + '</option>').join('')
        + '</select>'
      : '';

    return '<div class="cart-drawer-upsell__item" data-upsell-item data-default-price="' + this.escapeUpsellHtml(product.defaultPriceText) + '">'
      + '<a class="cart-drawer-upsell__image" href="' + this.escapeUpsellHtml(product.url) + '">' + imageMarkup + '</a>'
      + '<div class="cart-drawer-upsell__content">'
      + '<a class="cart-drawer-upsell__title" href="' + this.escapeUpsellHtml(product.url) + '">' + this.escapeUpsellHtml(product.title) + '</a>'
      + '<div class="cart-drawer-upsell__controls">'
      + '<div class="cart-drawer-upsell__meta">'
      + '<p class="cart-drawer-upsell__price" data-upsell-price>' + this.escapeUpsellHtml(product.defaultPriceText) + '</p>'
      + variantSelectMarkup
      + '</div>'
      + '<button type="button" class="cart-drawer-upsell__add button button--secondary" data-upsell-add data-add-label="+ Add" '
      + (hasMultipleVariants ? 'disabled aria-disabled="true"' : 'data-variant-id="' + product.variants[0].id + '"')
      + '><span data-upsell-add-label>+ Add</span></button>'
      + '</div>'
      + '<p class="cart-drawer-upsell__error caption-with-letter-spacing" data-upsell-error hidden></p>'
      + '</div>'
      + '</div>';
  }



  requestUpsellJson(url, options = {}) {
    if (typeof window.fetch === 'function') {
      return window.fetch(url, options).then(async (response) => ({
        ok: response.ok,
        data: await response.json(),
      }));
    }

    return new Promise((resolve, reject) => {
      if (typeof XMLHttpRequest !== 'function') {
        reject(new Error('Browser request API unavailable.'));
        return;
      }

      const request = new XMLHttpRequest();
      request.open(options.method || 'GET', url, true);

      Object.entries(options.headers || {}).forEach(([key, value]) => {
        request.setRequestHeader(key, value);
      });

      request.onload = () => {
        try {
          resolve({
            ok: request.status >= 200 && request.status < 300,
            data: JSON.parse(request.responseText || '{}'),
          });
        } catch (error) {
          reject(error);
        }
      };

      request.onerror = () => reject(new Error('Network request failed.'));
      request.send(options.body || null);
    });
  }

  getUpsellStoreUrl(path) {
    const root =
      window.Shopify?.routes?.root ||
      window.routes?.root ||
      '/';

    return root + String(path).replace(/^\/+/, '');
  }

  formatUpsellMoney(value) {
    return '£' + Number(value).toFixed(2);
  }

  escapeUpsellHtml(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  handleUpsellVariantChange(event) {
    const variantSelect = event.target.closest('[data-upsell-variant-select]');
    if (!variantSelect) return;

    const upsellItem = variantSelect.closest('[data-upsell-item]');
    if (!upsellItem) return;

    const addButton = upsellItem.querySelector('[data-upsell-add]');
    const priceElement = upsellItem.querySelector('[data-upsell-price]');
    const selectedOption = variantSelect.options[variantSelect.selectedIndex];
    const defaultPrice = upsellItem.dataset.defaultPrice || '';

    if (priceElement) {
      priceElement.textContent = selectedOption?.dataset.price || defaultPrice;
    }

    this.clearUpsellError(upsellItem);

    if (!addButton) return;

    if (variantSelect.value) {
      addButton.dataset.variantId = variantSelect.value;
      addButton.disabled = false;
      addButton.removeAttribute('aria-disabled');
      return;
    }

    delete addButton.dataset.variantId;
    addButton.disabled = true;
    addButton.setAttribute('aria-disabled', 'true');
  }

  async handleUpsellAdd(event) {
    const addButton = event.target.closest('[data-upsell-add]');
    if (!addButton) return;

    event.preventDefault();

    if (addButton.dataset.loading === 'true') return;

    const upsellItem = addButton.closest('[data-upsell-item]');
    if (!upsellItem) return;

    const variantSelect = upsellItem.querySelector('[data-upsell-variant-select]');
    const variantId = addButton.dataset.variantId || variantSelect?.value;

    if (!variantId) {
      this.setUpsellError(upsellItem, 'Please choose an option');
      return;
    }

    this.clearUpsellError(upsellItem);
    this.setUpsellLoading(addButton, true);
    this.setActiveElement(addButton);

    try {
      const cartAddUrl = this.getUpsellStoreUrl(
        ((typeof routes !== 'undefined' && routes.cart_add_url) ? routes.cart_add_url : 'cart/add') + '.js'
      );
      const addResponse = await this.requestUpsellJson(cartAddUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: [
            {
              id: Number(variantId),
              quantity: 1,
            },
          ],
          sections: this.getSectionsToRender().map((section) => section.id),
          sections_url: window.location.pathname,
        }),
      });

      const parsedState = addResponse.data;

      if (!addResponse.ok || parsedState.status) {
        throw new Error(parsedState.description || parsedState.message || 'Unable to add this item right now.');
      }

      this.renderContents(parsedState);
    } catch (error) {
      this.setUpsellLoading(addButton, false);
      this.setUpsellError(upsellItem, error.message || 'Unable to add this item right now.');
    }
  }

  setUpsellLoading(addButton, isLoading) {
    if (!addButton) return;

    const addLabel = addButton.querySelector('[data-upsell-add-label]');
    const defaultLabel = addButton.dataset.addLabel || '+ Add';

    addButton.dataset.loading = isLoading ? 'true' : 'false';
    addButton.disabled = isLoading;

    if (!isLoading && addButton.dataset.variantId == null && addButton.closest('[data-upsell-item]')?.querySelector('[data-upsell-variant-select]')) {
      addButton.setAttribute('aria-disabled', 'true');
    } else if (addButton.dataset.variantId != null) {
      addButton.removeAttribute('aria-disabled');
    }

    if (addLabel) {
      addLabel.textContent = isLoading ? 'Adding...' : defaultLabel;
    }
  }

  setUpsellError(upsellItem, message) {
    const errorElement = upsellItem?.querySelector('[data-upsell-error]');
    if (!errorElement) return;

    errorElement.textContent = message;
    errorElement.hidden = false;
  }

  clearUpsellError(upsellItem) {
    const errorElement = upsellItem?.querySelector('[data-upsell-error]');
    if (!errorElement) return;

    errorElement.textContent = '';
    errorElement.hidden = true;
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
